import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useBlocker } from "react-router-dom";
import { getFinancialSeasons } from "../services/api/financialService";

const EMPTY_PAGE_INFO = Object.freeze({
  currentPage: 1,
  pageSize: 25,
  totalPages: 1,
  hasPreviousPage: false,
  hasNextPage: false,
});

const FinancialSeasonContext = createContext(null);
const FinancialDraftContext = createContext(null);
const FinancialSalesVersionContext = createContext(null);
const FinancialDailyExpenseVersionContext = createContext(null);

const isCanceled = error =>
  error?.name === "AbortError" || error?.name === "CanceledError" || error?.code === "ERR_CANCELED";

export const FinancialSeasonProvider = ({ children }) => {
  const [siteId, setSiteId] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [seasonPageInfo, setSeasonPageInfo] = useState(EMPTY_PAGE_INFO);
  const [seasonsLoading, setSeasonsLoading] = useState(false);
  const [seasonsLoaded, setSeasonsLoaded] = useState(false);
  const [seasonError, setSeasonError] = useState(null);
  const [salesDataVersionBySeason, setSalesDataVersionBySeason] = useState({});
  const [dailyExpenseDataVersionBySeason, setDailyExpenseDataVersionBySeason] = useState({});
  const [financialDraftDirty, setFinancialDraftDirty] = useState(false);
  const [opexDraftDirty, setOpexDraftDirty] = useState(false);
  const [salesDraftDirty, setSalesDraftDirty] = useState(false);
  const [dailyExpenseDraftDirty, setDailyExpenseDraftDirty] = useState(false);
  const [discardPrompt, setDiscardPrompt] = useState(null);
  const requestRef = useRef({ id: 0, controller: null });
  const siteIdRef = useRef(null);
  const selectedSeasonIdRef = useRef("");
  const selectionVersionRef = useRef(0);
  const draftDirtyRef = useRef({ opex: false, sales: false, dailyExpenses: false });
  const draftSignatureRef = useRef("false:false:false");
  const draftVersionRef = useRef(0);
  const draftDiscardHandlersRef = useRef(new Map());

  const draftSignature = `${opexDraftDirty}:${salesDraftDirty}:${dailyExpenseDraftDirty}`;
  if (draftSignatureRef.current !== draftSignature) {
    draftSignatureRef.current = draftSignature;
    draftVersionRef.current += 1;
  }

  draftDirtyRef.current = {
    opex: opexDraftDirty,
    sales: salesDraftDirty,
    dailyExpenses: dailyExpenseDraftDirty,
  };

  useEffect(() => () => requestRef.current.controller?.abort(), []);

  /**
   * Presents one shared confirmation for Season-scoped drafts that would be
   * discarded by a Financial Season context change. CAPEX is intentionally
   * excluded because it is Site-scoped and survives Season changes.
   */
  const confirmSeasonContextChange = useCallback(
    ({
      reason = "Changing Financial Seasons",
      includeOpex = true,
      includeSales = true,
      includeDailyExpenses = true,
    } = {}) => {
      const dirtyAreas = [];
      const currentDrafts = draftDirtyRef.current;

      if (includeOpex && currentDrafts.opex) dirtyAreas.push("Monthly OPEX");
      if (includeSales && currentDrafts.sales) dirtyAreas.push("Sales");
      if (includeDailyExpenses && currentDrafts.dailyExpenses) dirtyAreas.push("Daily Expense");

      if (dirtyAreas.length === 0) return Promise.resolve(true);

      const description =
        dirtyAreas.length === 1
          ? `${dirtyAreas[0]} draft`
          : `${dirtyAreas.slice(0, -1).join(", ")} and ${dirtyAreas.at(-1)} drafts`;

      return new Promise(resolve => {
        setDiscardPrompt({
          title: "Discard unsaved changes?",
          message: `${reason} will discard your unsaved ${description}. This cannot be undone.`,
          resolve,
        });
      });
    },
    []
  );

  const getSeasonDraftVersion = useCallback(() => draftVersionRef.current, []);

  /** Records a content change within an existing Season-bound draft. */
  const markSeasonDraftChanged = useCallback(() => {
    draftVersionRef.current += 1;
  }, []);

  const registerDraftDiscardHandler = useCallback((key, handler) => {
    draftDiscardHandlersRef.current.set(key, handler);
    return () => draftDiscardHandlersRef.current.delete(key);
  }, []);

  const discardRegisteredDrafts = useCallback(() => {
    draftDiscardHandlersRef.current.forEach(handler => handler());
  }, []);

  /**
   * Commits one bounded Season page. If the page would replace the selected
   * Season, the shared draft guard runs before any page/selection state moves.
   */
  const commitPage = useCallback(
    async (data, preferredSeasonId, { skipDraftConfirmation = false, confirmedDraftVersion = null, reason } = {}) => {
      const nextSeasons = Array.isArray(data?.seasons) ? data.seasons : [];
      const nextActive = data?.activeSeason || null;
      const preferred =
        nextSeasons.find(season => String(season.id) === String(preferredSeasonId || "")) ||
        nextSeasons.find(season => String(season.id) === String(nextActive?.id || "")) ||
        nextSeasons[0] ||
        null;
      const currentSelectedId = String(selectedSeasonIdRef.current || "");
      const currentStillVisible = nextSeasons.some(season => String(season.id) === currentSelectedId);
      const nextSelectedId = currentStillVisible ? currentSelectedId : preferred ? String(preferred.id) : "";
      const selectionWillChange = nextSelectedId !== currentSelectedId;
      const draftSafetyChanged = confirmedDraftVersion !== null && confirmedDraftVersion !== draftVersionRef.current;

      if (
        selectionWillChange &&
        (!skipDraftConfirmation || draftSafetyChanged) &&
        !(await confirmSeasonContextChange({
          reason: reason || "Changing Financial Season pages",
        }))
      ) {
        return null;
      }

      setSeasons(nextSeasons);
      setActiveSeason(nextActive);
      setSeasonPageInfo({ ...EMPTY_PAGE_INFO, ...(data?.pageInfo || {}) });
      selectedSeasonIdRef.current = nextSelectedId;
      if (selectionWillChange) selectionVersionRef.current += 1;
      setSelectedSeasonId(nextSelectedId);
      setSeasonsLoaded(true);
      return {
        seasons: nextSeasons,
        activeSeason: nextActive,
        selectedSeason:
          preferred && String(preferred.id) === nextSelectedId
            ? preferred
            : nextSeasons.find(season => String(season.id) === nextSelectedId) || null,
        selectedSeasonId: nextSelectedId,
        pageInfo: data?.pageInfo || EMPTY_PAGE_INFO,
      };
    },
    [confirmSeasonContextChange]
  );

  const refreshSeasons = useCallback(
    async (options = {}) => {
      const targetSiteId = options.siteId ?? siteIdRef.current;
      if (!targetSiteId) return null;

      requestRef.current.controller?.abort();
      const controller = new AbortController();
      const requestId = requestRef.current.id + 1;
      const selectionVersion = selectionVersionRef.current;
      requestRef.current = { id: requestId, controller };
      setSeasonsLoading(true);
      setSeasonError(null);

      try {
        const data = await getFinancialSeasons(targetSiteId, {
          page: options.page || 1,
          limit: 25,
          focusSeasonId: options.focusSeasonId || null,
          preferActive: Boolean(options.preferActive),
          signal: controller.signal,
        });
        if (
          requestRef.current.id !== requestId ||
          String(siteIdRef.current) !== String(targetSiteId) ||
          selectionVersionRef.current !== selectionVersion
        ) {
          return null;
        }
        return await commitPage(data, options.focusSeasonId, {
          skipDraftConfirmation: Boolean(options.skipDraftConfirmation),
          confirmedDraftVersion: options.confirmedDraftVersion ?? null,
          reason: options.selectionChangeReason,
        });
      } catch (error) {
        if (isCanceled(error) || requestRef.current.id !== requestId) return null;
        console.error("Error loading Financial Seasons:", error);
        setSeasonsLoaded(false);
        setSeasonError("Financial Seasons could not be loaded.");
        return null;
      } finally {
        if (requestRef.current.id === requestId) {
          requestRef.current.controller = null;
          setSeasonsLoading(false);
        }
      }
    },
    [commitPage]
  );

  const ensureSite = useCallback(
    targetSiteId => {
      const normalized = targetSiteId == null ? null : String(targetSiteId);
      if (normalized === siteIdRef.current) return;

      requestRef.current.controller?.abort();
      requestRef.current.id += 1;
      selectionVersionRef.current += 1;
      siteIdRef.current = normalized;
      setSiteId(normalized);
      setSeasons([]);
      setActiveSeason(null);
      selectedSeasonIdRef.current = "";
      setSelectedSeasonId("");
      setSeasonPageInfo(EMPTY_PAGE_INFO);
      setSeasonsLoaded(false);
      setSeasonError(null);
      setSalesDataVersionBySeason({});
      setDailyExpenseDataVersionBySeason({});
      setFinancialDraftDirty(false);
      setOpexDraftDirty(false);
      setSalesDraftDirty(false);
      setDailyExpenseDraftDirty(false);

      if (normalized) void refreshSeasons({ siteId: normalized, page: 1, preferActive: true });
    },
    [refreshSeasons]
  );

  /**
   * Changes the shared selected Season. Callers that already ran the shared
   * confirmation can pass skipDraftConfirmation to avoid a second dialog.
   * Sales keeps its existing local draft confirmation, so the default guard
   * protects OPEX + Daily Expense without duplicating the Sales prompt.
   */
  const selectSeason = useCallback(
    async (seasonId, options = {}) => {
      const nextSeasonId = seasonId ? String(seasonId) : "";
      const currentSeasonId = String(selectedSeasonIdRef.current || "");

      if (nextSeasonId === currentSeasonId) return true;

      if (
        !options.skipDraftConfirmation &&
        !(await confirmSeasonContextChange({
          reason: options.reason || "Switching Financial Seasons",
          includeSales: options.includeSales !== false,
        }))
      ) {
        return false;
      }

      selectedSeasonIdRef.current = nextSeasonId;
      selectionVersionRef.current += 1;
      setSelectedSeasonId(nextSeasonId);
      return true;
    },
    [confirmSeasonContextChange]
  );
  const markSalesChanged = useCallback(seasonId => {
    if (!seasonId) return;
    const key = String(seasonId);
    setSalesDataVersionBySeason(previous => ({ ...previous, [key]: (previous[key] || 0) + 1 }));
  }, []);

  const markDailyExpensesChanged = useCallback((seasonId, expectedSiteId = null) => {
    if (!seasonId) return;

    if (
      expectedSiteId !== null &&
      expectedSiteId !== undefined &&
      String(siteIdRef.current ?? "") !== String(expectedSiteId)
    ) {
      return;
    }

    const key = String(seasonId);
    setDailyExpenseDataVersionBySeason(previous => ({
      ...previous,
      [key]: (previous[key] || 0) + 1,
    }));
  }, []);

  const selectedSeason = useMemo(
    () => seasons.find(season => String(season.id) === String(selectedSeasonId)) || null,
    [seasons, selectedSeasonId]
  );

  const value = useMemo(
    () => ({
      siteId,
      seasons,
      setSeasons,
      activeSeason,
      setActiveSeason,
      selectedSeasonId,
      setSelectedSeasonId: selectSeason,
      selectedSeason,
      seasonPageInfo,
      setSeasonPageInfo,
      seasonsLoading,
      setSeasonsLoading,
      seasonsLoaded,
      setSeasonsLoaded,
      seasonError,
      setSeasonError,
      ensureSite,
      refreshSeasons,
      selectSeason,
      confirmSeasonContextChange,
      getSeasonDraftVersion,
    }),
    [
      activeSeason,
      ensureSite,
      refreshSeasons,
      confirmSeasonContextChange,
      getSeasonDraftVersion,
      seasonError,
      seasonPageInfo,
      seasons,
      seasonsLoaded,
      seasonsLoading,
      selectSeason,
      selectedSeason,
      selectedSeasonId,
      siteId,
    ]
  );
  const draftValue = useMemo(
    () => ({
      financialDraftDirty,
      setFinancialDraftDirty,
      opexDraftDirty,
      setOpexDraftDirty,
      salesDraftDirty,
      setSalesDraftDirty,
      dailyExpenseDraftDirty,
      setDailyExpenseDraftDirty,
      markSeasonDraftChanged,
      registerDraftDiscardHandler,
    }),
    [
      dailyExpenseDraftDirty,
      financialDraftDirty,
      markSeasonDraftChanged,
      opexDraftDirty,
      registerDraftDiscardHandler,
      salesDraftDirty,
    ]
  );
  const salesVersionValue = useMemo(
    () => ({
      salesDataVersionBySeason,
      markSalesChanged,
    }),
    [markSalesChanged, salesDataVersionBySeason]
  );
  const dailyExpenseVersionValue = useMemo(
    () => ({
      dailyExpenseDataVersionBySeason,
      markDailyExpensesChanged,
    }),
    [dailyExpenseDataVersionBySeason, markDailyExpensesChanged]
  );

  return (
    <FinancialSeasonContext.Provider value={value}>
      <FinancialDraftContext.Provider value={draftValue}>
        <FinancialSalesVersionContext.Provider value={salesVersionValue}>
          <FinancialDailyExpenseVersionContext.Provider value={dailyExpenseVersionValue}>
            <UnsavedDraftNavigationGuard
              dirty={financialDraftDirty || salesDraftDirty || dailyExpenseDraftDirty}
              onDiscard={discardRegisteredDrafts}
            />
            {children}
            <Dialog
              open={Boolean(discardPrompt)}
              onClose={() => {
                discardPrompt?.resolve(false);
                setDiscardPrompt(null);
              }}
              fullWidth
              maxWidth="xs"
            >
              <DialogTitle>{discardPrompt?.title}</DialogTitle>
              <DialogContent>
                <Typography>{discardPrompt?.message}</Typography>
              </DialogContent>
              <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
                <Button
                  onClick={() => {
                    discardPrompt?.resolve(false);
                    setDiscardPrompt(null);
                  }}
                >
                  Keep editing
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    discardPrompt?.resolve(true);
                    setDiscardPrompt(null);
                  }}
                >
                  Discard changes
                </Button>
              </DialogActions>
            </Dialog>
          </FinancialDailyExpenseVersionContext.Provider>
        </FinancialSalesVersionContext.Provider>
      </FinancialDraftContext.Provider>
    </FinancialSeasonContext.Provider>
  );
};

const UnsavedDraftNavigationGuard = ({ dirty, onDiscard }) => {
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search ||
        currentLocation.hash !== nextLocation.hash),
    [dirty]
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    const handleBeforeUnload = event => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [dirty]);

  const staysOnPage = blocker.location?.pathname === window.location.pathname;

  return (
    <Dialog open={blocker.state === "blocked"} onClose={() => blocker.reset()} fullWidth maxWidth="xs">
      <DialogTitle>Discard unsaved changes?</DialogTitle>
      <DialogContent>
        <Typography>
          {staysOnPage
            ? "Changing financial sections will discard your unsaved changes."
            : "Leaving this page will discard your unsaved changes."}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button onClick={() => blocker.reset()}>Keep editing</Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            onDiscard();
            blocker.proceed();
          }}
        >
          Discard changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const useFinancialSeasons = (siteId, active = true) => {
  const context = useContext(FinancialSeasonContext);
  if (!context) throw new Error("useFinancialSeasons must be used within FinancialSeasonProvider");
  const { ensureSite } = context;

  useEffect(() => {
    if (active && siteId != null) ensureSite(siteId);
  }, [active, ensureSite, siteId]);

  return context;
};

export const useFinancialDraftStatus = () => {
  const context = useContext(FinancialDraftContext);
  if (!context) throw new Error("useFinancialDraftStatus must be used within FinancialSeasonProvider");
  return context;
};

export const useFinancialSalesVersions = () => {
  const context = useContext(FinancialSalesVersionContext);
  if (!context) throw new Error("useFinancialSalesVersions must be used within FinancialSeasonProvider");
  return context;
};

export const useFinancialDailyExpenseVersions = () => {
  const context = useContext(FinancialDailyExpenseVersionContext);
  if (!context) {
    throw new Error("useFinancialDailyExpenseVersions must be used within FinancialSeasonProvider");
  }
  return context;
};
/** @file Provides shared Financial Season selection and refresh state to financial views. */
